<template>
    <v-card>
        <!-- En-tête -->
        <v-card-title class="warning white--text py-3 px-4">
            <v-icon color="white" class="mr-2">mdi-alert</v-icon>
            <span class="subtitle-1 font-weight-bold">Acteurs similaires trouvés</span>
            <v-spacer></v-spacer>
            <v-chip small color="white" text-color="warning" class="font-weight-bold mr-2">
                {{ totalCount }} résultat{{ totalCount > 1 ? 's' : '' }}
            </v-chip>
            <v-btn icon small @click="$emit('cancel')" title="Fermer">
                <v-icon color="white">mdi-close</v-icon>
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
            <!-- Message + bouton fermer en haut -->
            <div class="px-4 pt-3 pb-2 d-flex align-center justify-space-between">
                <p class="body-2 mb-0 grey--text text--darken-2">
                    Vérifiez qu'il ne s'agit pas d'un doublon avant de continuer.
                </p>
                <v-btn text color="info" rounded small class="ml-3 flex-shrink-0" @click="$emit('cancel')">
                    <v-icon left small>mdi-close</v-icon>
                    Ignorer et continuer
                </v-btn>
            </div>

            <v-divider></v-divider>

            <!-- ── Section 1 : correspondances par numéro de téléphone ── -->
            <template v-if="contactMatches.length > 0">
                <div class="px-4 py-2 d-flex align-center" style="background:#fff3e0;">
                    <v-icon color="orange darken-2" class="mr-2" small>mdi-phone-alert</v-icon>
                    <span class="caption font-weight-bold orange--text text--darken-2">
                        Même numéro de téléphone déjà enregistré
                    </span>
                </div>
                <v-list two-line class="py-0">
                    <template v-for="(item, index) in contactMatches">
                        <v-list-item :key="'contact-' + item.id" class="px-4 py-2">
                            <v-list-item-avatar size="48" color="orange lighten-4">
                                <v-img v-if="item.photo_url" :src="`/${item.photo_url}`"></v-img>
                                <v-icon v-else color="orange darken-2">mdi-account</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold">
                                    {{ item.full_name }}
                                    <v-chip x-small color="orange darken-2" text-color="white" class="ml-2">
                                        même tél.
                                    </v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle class="mt-1">
                                    <span v-if="item.gender" class="mr-3">
                                        <v-icon x-small>mdi-gender-{{ item.gender === 'Homme' ? 'male' : 'female' }}</v-icon>
                                        {{ item.gender }}
                                    </span>
                                    <span v-if="item.age" class="mr-3">
                                        <v-icon x-small>mdi-cake</v-icon>
                                        {{ item.age }} ans
                                    </span>
                                    <span v-if="item.contact" class="mr-3">
                                        <v-icon x-small color="orange darken-2">mdi-phone</v-icon>
                                        <strong>{{ item.contact }}</strong>
                                    </span>
                                    <span v-if="item.organization" class="mr-3">
                                        <v-icon x-small>mdi-domain</v-icon>
                                        {{ item.organization }}
                                    </span>
                                    <span v-if="item.municipality">
                                        <v-icon x-small>mdi-map-marker</v-icon>
                                        {{ item.municipality }}<span v-if="item.town"> / {{ item.town }}</span>
                                    </span>
                                </v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-action>
                                <v-btn icon small @click="viewActor(item)" title="Voir la fiche">
                                    <v-icon color="orange darken-2">mdi-open-in-new</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                        <v-divider v-if="index < contactMatches.length - 1" :key="'cdiv-' + item.id"></v-divider>
                    </template>
                </v-list>
            </template>

            <!-- ── Section 2 : correspondances par similarité de nom ── -->
            <template v-if="nameMatches.length > 0">
                <v-divider v-if="contactMatches.length > 0"></v-divider>
                <div class="px-4 py-2 d-flex align-center" style="background:#f3f3f3;">
                    <v-icon color="grey darken-2" class="mr-2" small>mdi-account-search</v-icon>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                        Nom similaire dans la même localité
                    </span>
                </div>
                <v-list two-line class="py-0">
                    <template v-for="(item, index) in nameMatches">
                        <v-list-item :key="'name-' + item.id" class="px-4 py-2">
                            <v-list-item-avatar size="48" color="grey lighten-3">
                                <v-img v-if="item.photo_url" :src="`/${item.photo_url}`"></v-img>
                                <v-icon v-else color="grey">mdi-account</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold">
                                    {{ item.full_name }}
                                    <v-chip x-small :color="scoreColor(item.score_percent)" text-color="white" class="ml-2">
                                        {{ item.score_percent }}% similaire
                                    </v-chip>
                                </v-list-item-title>
                                <v-list-item-subtitle class="mt-1">
                                    <span v-if="item.gender" class="mr-3">
                                        <v-icon x-small>mdi-gender-{{ item.gender === 'Homme' ? 'male' : 'female' }}</v-icon>
                                        {{ item.gender }}
                                    </span>
                                    <span v-if="item.age" class="mr-3">
                                        <v-icon x-small>mdi-cake</v-icon>
                                        {{ item.age }} ans
                                    </span>
                                    <span v-if="item.contact" class="mr-3">
                                        <v-icon x-small>mdi-phone</v-icon>
                                        {{ item.contact }}
                                    </span>
                                    <span v-if="item.organization" class="mr-3">
                                        <v-icon x-small>mdi-domain</v-icon>
                                        {{ item.organization }}
                                    </span>
                                    <span v-if="item.municipality">
                                        <v-icon x-small>mdi-map-marker</v-icon>
                                        {{ item.municipality }}<span v-if="item.town"> / {{ item.town }}</span>
                                    </span>
                                </v-list-item-subtitle>

                                <!-- Barres de signaux -->
                                <div v-if="item.signals" class="mt-2 d-flex flex-wrap" style="gap: 6px;">
                                    <v-tooltip bottom v-for="(val, key) in item.signals" :key="key">
                                        <template v-slot:activator="{ on }">
                                            <div v-on="on" class="d-flex align-center" style="font-size:11px; gap:3px;">
                                                <span class="grey--text text--darken-1">{{ signalLabel(key) }}</span>
                                                <div style="width:40px; height:5px; border-radius:3px; background:#e0e0e0; overflow:hidden;">
                                                    <div :style="{ width: val + '%', height: '100%', background: signalBarColor(val), borderRadius: '3px' }"></div>
                                                </div>
                                            </div>
                                        </template>
                                        <span>{{ signalLabel(key) }} : {{ val }}%</span>
                                    </v-tooltip>
                                </div>
                            </v-list-item-content>

                            <v-list-item-action>
                                <v-btn icon small @click="viewActor(item)" title="Voir la fiche">
                                    <v-icon color="primary">mdi-open-in-new</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                        <v-divider v-if="index < nameMatches.length - 1" :key="'ndiv-' + item.id"></v-divider>
                    </template>
                </v-list>
            </template>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Actions bas -->
        <v-card-actions class="pa-4">
            <div class="body-2 grey--text">
                <v-icon small color="grey">mdi-information-outline</v-icon>
                Si c'est une personne différente, continuez normalement.
            </div>
            <v-spacer></v-spacer>
            <v-btn text color="info" rounded @click="$emit('cancel')">
                <v-icon left small>mdi-close</v-icon>
                Ignorer et continuer
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
export default {
    name: 'duplicate-suggestion',

    props: {
        contactMatches: { type: Array, default: () => [] },
        nameMatches:    { type: Array, default: () => [] },
    },

    computed: {
        totalCount() {
            return this.contactMatches.length + this.nameMatches.length
        }
    },

    methods: {
        viewActor(item) {
            const route = this.$router.resolve({ name: 'actors.show', params: { actorId: item.id } })
            window.open(route.href, '_blank')
        },

        scoreColor(score) {
            if (score >= 90) return 'error'
            if (score >= 75) return 'warning'
            return 'primary'
        },

        signalBarColor(val) {
            if (val >= 80) return '#4CAF50'
            if (val >= 50) return '#FF9800'
            return '#F44336'
        },

        signalLabel(key) {
            const labels = {
                name:         'Nom',
                age:          'Âge',
                organization: 'Org.',
                gender:       'Sexe',
                contact:      'Tél.',
            }
            return labels[key] || key
        }
    }
}
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/DuplicateSuggestion.vue